import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { HeroApp } from "../hero/HeroApp.js";
import { loadStoredBrandConfig } from "../config/brand.js";
import { localBusinessJsonLd } from "../config/seo.js";
import { useAuth } from "../context/AuthContext.jsx";

function resetHomeScrollUiState() {
  window.scrollTo(0, 0);
  document.documentElement.classList.remove(
    "has-floating-card-subheader",
    "hero-cards-docked-in-header",
  );
  if (document.documentElement.dataset.storyPanel != null) {
    delete document.documentElement.dataset.storyPanel;
  }
}

export default function HomePage() {
  const config = useMemo(() => loadStoredBrandConfig(), []);
  const hostRef = useRef(null);
  const loadingRef = useRef(null);
  const heroRef = useRef(null);
  const { isAuthenticated, ready } = useAuth();

  /**
   * SPA navigation + browser scroll restoration can leave the window scrolled
   * deep into the story (mobile card stack) or html classes from a torn-down hero.
   */
  useLayoutEffect(() => {
    resetHomeScrollUiState();
    const raf = requestAnimationFrame(() => {
      resetHomeScrollUiState();
    });
    const t = window.setTimeout(() => {
      resetHomeScrollUiState();
    }, 0);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) resetHomeScrollUiState();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    if (!ready) return undefined;
    const rootEl = hostRef.current;
    const loadingEl = loadingRef.current;
    if (!rootEl || !loadingEl) return undefined;

    const hero = new HeroApp({
      rootEl,
      loadingEl,
      brandConfig: config,
      siteSettingsEnabled: isAuthenticated,
    });
    heroRef.current = hero;
    requestAnimationFrame(() => {
      resetHomeScrollUiState();
      hero.onScroll();
    });
    return () => {
      hero.dispose();
      heroRef.current = null;
    };
  }, [config, ready, isAuthenticated]);

  return (
    <div className="hero-route-root">
      {/* LocalBusiness structured data — rendered only on the homepage (this
          component), so service pages don't duplicate the schema. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessJsonLd) }}
      />
      <div ref={loadingRef} className="loading-screen">
        <div className="loading-card">
          <p data-brand-loading-title>AI &amp; Computer Services</p>
          {/* Decorative loading headline — the real page H1 lives in the SEO
              content section below so the page has exactly one H1. */}
          <p className="loading-card__headline">{config.identity.headline}</p>
          <p className="loading-card__message" data-brand-loading-message>
            {config.identity.loadingMessage}
          </p>
        </div>
      </div>
      <div ref={hostRef} className="hero-host" />

      <section className="home-seo" aria-labelledby="home-seo-h1">
        <div className="home-seo__inner">
          <h1 id="home-seo-h1" className="home-seo__h1">
            AI, Software, Web Development &amp; IT Support in Corona, CA
          </h1>
          <p className="site-page__lead">
            AICSNetworks helps small businesses in Corona, CA and the surrounding
            area with AI receptionists and chatbots, custom software and business
            websites, workflow automation, IT support, and network installation —
            built and supported by a local team.
          </p>

          <section className="site-page__section">
            <h2 className="site-page__section-title">
              AI and Automation Services in Corona, CA
            </h2>
            <p className="site-page__text">
              Capture every lead and cut repetitive work with AI answering and
              automation tuned to how your business runs.
            </p>
            <ul className="home-seo__links">
              <li>
                <Link to="/services/ai-receptionist-corona">
                  AI Receptionist Setup
                </Link>
              </li>
              <li>
                <Link to="/services/ai-chatbot-development-corona">
                  AI Chatbot Development
                </Link>
              </li>
              <li>
                <Link to="/services/workflow-automation-corona">
                  Workflow Automation
                </Link>
              </li>
            </ul>
          </section>

          <section className="site-page__section">
            <h2 className="site-page__section-title">
              Business Websites and Custom Software
            </h2>
            <p className="site-page__text">
              Fast, custom websites and software built around your workflow — not
              template bloat or off-the-shelf compromises.
            </p>
            <ul className="home-seo__links">
              <li>
                <Link to="/services/business-website-development-corona">
                  Business Website Development
                </Link>
              </li>
              <li>
                <Link to="/services/custom-software-development-corona">
                  Custom Software Development
                </Link>
              </li>
            </ul>
          </section>

          <section className="site-page__section">
            <h2 className="site-page__section-title">
              IT Support and Network Installation
            </h2>
            <p className="site-page__text">
              Dependable day-to-day IT and properly installed networks that hold
              up under load — on-site in Corona when you need it.
            </p>
            <ul className="home-seo__links">
              <li>
                <Link to="/services/it-support-corona">
                  Small Business IT Support
                </Link>
              </li>
              <li>
                <Link to="/services/network-installation-corona">
                  Network Installation
                </Link>
              </li>
            </ul>
          </section>

          <section className="site-page__section">
            <h2 className="site-page__section-title">
              How AICSNetworks Helps Local Businesses
            </h2>
            <p className="site-page__text">
              We start with a discovery conversation, scope the work clearly, and
              deliver in stages — then stay available as your business changes.
              One local point of contact across AI, software, web, and IT.
            </p>
          </section>

          <section className="site-page__section">
            <h2 className="site-page__section-title">Contact AICSNetworks</h2>
            <p className="site-page__text">
              Serving Corona, CA and nearby communities. Tell us what you&apos;re
              trying to accomplish and we&apos;ll help you scope it.
            </p>
            <p>
              <Link to="/contact" className="site-button-primary">
                Contact AICSNetworks
              </Link>
            </p>
          </section>
        </div>
      </section>
    </div>
  );
}
